import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { type MonthlyReport } from "@avg/shared";
import { useAuth } from "../auth/AuthContext";
import { MonthSwitcher } from "../components/MonthSwitcher";
import { StatusBadge } from "../components/StatusBadge";
import { defaultReportingMonth } from "../lib/reportingWindow";
import { parseISO } from "../lib/dates";
import {
  useI18n,
  useMonthLabel,
  useT,
  useWageCodeLabel,
  type TranslateFn,
} from "../i18n/I18nContext";

export function TeamPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const queryClient = useQueryClient();
  const t = useT();
  const monthLabel = useMonthLabel();
  const [{ year, month }, setMonth] = useState(defaultReportingMonth());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const teamQuery = useQuery({
    queryKey: ["team-reports", year, month],
    queryFn: () => api.getTeamReports(year, month),
  });
  const wageCodesQuery = useQuery({
    queryKey: ["wage-codes"],
    queryFn: () => api.listWageCodes(),
  });
  const wageCodes = wageCodesQuery.data?.wageCodes ?? [];

  const reports = teamQuery.data?.reports ?? [];
  const selected = useMemo(
    () => reports.find((r) => r.id === selectedId) ?? null,
    [reports, selectedId],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["team-reports"] });
  };

  const managerApprove = useMutation({
    mutationFn: (id: string) => api.managerApprove(id),
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });
  const adminApprove = useMutation({
    mutationFn: (id: string) => api.adminApprove(id),
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.rejectReport(id, reason),
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, draft: 0, rejected: 0 };
    for (const r of reports) {
      if (r.status === "SUBMITTED") c.pending++;
      else if (r.status === "MANAGER_APPROVED" || r.status === "ADMIN_APPROVED") c.approved++;
      else if (r.status === "REJECTED") c.rejected++;
      else c.draft++;
    }
    return c;
  }, [reports]);

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-portal-primary sm:text-3xl">
            {isAdmin ? t("team.title.admin") : t("team.title.manager")} —{" "}
            {monthLabel(month)} {year}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            {isAdmin ? t("team.subtitle.admin") : t("team.subtitle.manager")}
          </p>
        </div>
        <MonthSwitcher year={year} month={month} onChange={(y, m) => setMonth({ year: y, month: m })} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t("team.stat.draft")} value={counts.draft} tone="neutral" />
        <Stat label={t("team.stat.submitted")} value={counts.pending} tone="warn" />
        <Stat label={t("team.stat.approved")} value={counts.approved} tone="success" />
        <Stat label={t("team.stat.rejected")} value={counts.rejected} tone="danger" />
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}{" "}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t("common.close")}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(0,420px)]">
        <div className="card divide-y divide-border">
          {teamQuery.isLoading && (
            <div className="p-6 text-text-secondary">{t("app.loading")}</div>
          )}
          {!teamQuery.isLoading && reports.length === 0 && (
            <div className="p-6 text-text-secondary">{t("team.empty")}</div>
          )}
          {reports.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              className={`flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition ${
                selectedId === r.id ? "bg-portal-surface-tint" : "hover:bg-surface-alt"
              }`}
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-text-primary">
                  {r.user?.name ?? t("common.unknown")}
                </div>
                <div className="text-xs text-text-secondary">
                  {r.user?.employeeId ?? t("team.missingEmployeeId")} ·{" "}
                  {r.noDeviations
                    ? t("history.noDeviations")
                    : t("team.deviations_count", {
                        count: r.entries?.length ?? 0,
                      })}
                </div>
              </div>
              <StatusBadge status={r.status} />
            </button>
          ))}
        </div>

        <ReportDetailPanel
          report={selected}
          wageCodes={wageCodesQuery.data?.wageCodes ?? []}
          isAdmin={isAdmin ?? false}
          onManagerApprove={() => selected && managerApprove.mutate(selected.id)}
          onAdminApprove={() => selected && adminApprove.mutate(selected.id)}
          onReject={(reason) => selected && reject.mutate({ id: selected.id, reason })}
          t={t}
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "warn" | "success" | "danger";
}) {
  const colors: Record<string, string> = {
    neutral: "text-text-primary",
    warn: "text-amber-700",
    success: "text-portal-green-900",
    danger: "text-red-700",
  };
  return (
    <div className="card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className={`mt-1 font-display text-2xl ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function ReportDetailPanel({
  report,
  wageCodes,
  isAdmin,
  onManagerApprove,
  onAdminApprove,
  onReject,
  t,
}: {
  report: MonthlyReport | null;
  wageCodes: { code: string; description: string }[];
  isAdmin: boolean;
  onManagerApprove: () => void;
  onAdminApprove: () => void;
  onReject: (reason: string) => void;
  t: TranslateFn;
}) {
  const [reason, setReason] = useState("");
  const { locale } = useI18n();
  const wageCodeLabel = useWageCodeLabel();

  if (!report) {
    return (
      <div className="card grid place-items-center px-6 py-10 text-center text-sm text-text-secondary">
        {t("team.detail.empty")}
      </div>
    );
  }

  const canManagerApprove = report.status === "SUBMITTED";
  const canAdminApprove =
    isAdmin && (report.status === "SUBMITTED" || report.status === "MANAGER_APPROVED");
  const canReject = report.status === "SUBMITTED" || report.status === "MANAGER_APPROVED";

  return (
    <div className="card">
      <div className="border-b border-border px-5 py-4">
        <div className="text-sm font-semibold text-text-primary">{report.user?.name}</div>
        <div className="text-xs text-text-secondary">
          {report.user?.email}
          {report.user?.employeeId
            ? t("team.detail.employeeId", { id: report.user.employeeId })
            : t("team.detail.missingEmployeeId")}
        </div>
        <div className="mt-2">
          <StatusBadge status={report.status} />
        </div>
      </div>

      <div className="px-5 py-4">
        {report.noDeviations ? (
          <div className="rounded-md bg-portal-green-soft px-3 py-2 text-sm text-portal-green-900">
            {t("team.detail.markedNoDeviations")}{" "}
            <strong>{t("team.detail.noDeviationsLabel")}</strong>{" "}
            {t("team.detail.forMonth")}
          </div>
        ) : report.entries && report.entries.length > 0 ? (
          <ul className="space-y-3">
            {report.entries.map((e) => {
              const w = wageCodes.find((wc) => wc.code === e.wageCodeCode);
              return (
                <li key={e.id} className="flex items-start gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-portal-surface-tint text-xs font-bold text-portal-primary">
                    {e.wageCodeCode}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-text-primary">
                      {wageCodeLabel(e.wageCodeCode, w?.description ?? e.wageCodeCode)}
                      {e.hours ? <span className="text-text-secondary"> — {e.hours} h</span> : null}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {parseISO(e.fromDate).toLocaleDateString(locale, {
                        day: "numeric",
                        month: "short",
                      })}
                      {e.fromDate !== e.toDate &&
                        ` – ${parseISO(e.toDate).toLocaleDateString(locale, {
                          day: "numeric",
                          month: "short",
                        })}`}
                      {e.note && <span className="ml-2 italic text-text-muted">{e.note}</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-sm text-text-secondary">
            {t("team.detail.notReported")}
          </div>
        )}

        {report.rejectionReason && (
          <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            <strong>{t("team.detail.rejected")}</strong> {report.rejectionReason}
          </div>
        )}

        {(canManagerApprove || canAdminApprove || canReject) && (
          <div className="mt-5 space-y-2 border-t border-border pt-4">
            <div className="flex flex-wrap gap-2">
              {canManagerApprove && !isAdmin && (
                <button className="btn-success" onClick={onManagerApprove}>
                  {t("team.detail.approveManager")}
                </button>
              )}
              {canAdminApprove && (
                <button className="btn-success" onClick={onAdminApprove}>
                  {t("team.detail.approveAdmin")}
                </button>
              )}
            </div>
            {canReject && (
              <details>
                <summary className="cursor-pointer text-xs text-text-secondary">
                  {t("team.detail.rejectToggle")}
                </summary>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    className="input"
                    placeholder={t("team.detail.rejectReason")}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <button
                    className="btn-danger shrink-0"
                    disabled={!reason.trim()}
                    onClick={() => {
                      onReject(reason.trim());
                      setReason("");
                    }}
                  >
                    {t("team.detail.rejectButton")}
                  </button>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
